import { useEffect } from 'react';

import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
} from '../../../api/restaurant/restaurant.types';
import { Async, AsyncMap, Status } from '../../../types/data.types';
import { OrderType, Routes } from '../../../types/route.types';

interface UseRestaurantDetails {
  data: CollectionRestaurantDetail | DeliveryRestaurantDetail | null;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useRestaurantDetails = (
  loadCollectionLocationAreas: (prId: number) => void,
  loadRestaurantDetail: (vrId: number, type: OrderType, id: number) => void,
  collectionRestaurants: AsyncMap<CollectionRestaurantDetail>,
  deliveryRestaurants: AsyncMap<DeliveryRestaurantDetail>,
  routeParams: Routes['params'],
): UseRestaurantDetails => {
  const { type } = routeParams;
  const isCollection = type === OrderType.COLLECTION;
  const isDelivery = type === OrderType.DELIVERY;
  const vrId = Number(routeParams.vrid);
  const prId = isCollection ? Number(routeParams.id) : undefined;
  const deliveryLocationId = isDelivery ? Number(routeParams.id) : undefined;

  const loadRestaurantDetailFromRedux = (): Async<
    CollectionRestaurantDetail | DeliveryRestaurantDetail
  > => {
    const collectionRestaurantDetail = collectionRestaurants[prId];
    const deliveryRestaurantDetail =
      deliveryRestaurants[`del-${vrId}-${deliveryLocationId}`];

    if (isCollection && !!collectionRestaurantDetail?.status) {
      return collectionRestaurantDetail;
    }

    if (isDelivery && !!deliveryRestaurantDetail?.status) {
      return deliveryRestaurantDetail;
    }

    return { status: Status.LOADING };
  };

  const restaurantDetail = loadRestaurantDetailFromRedux();
  const isError = restaurantDetail.status === Status.ERROR;
  const isLoading = restaurantDetail.status === Status.LOADING;
  const isSuccess = restaurantDetail.status === Status.SUCCESS;
  const data = isSuccess ? restaurantDetail.data : null;

  useEffect(() => {
    if (!isSuccess) {
      // * Redux state - collectionRestaurants[prId] | deliveryRestaurants[del-vrId-deliveryLocationId]
      // - set restaurant collection/delivery details
      const id = isDelivery ? deliveryLocationId : prId;
      loadRestaurantDetail(vrId, type, id);
    }
  }, []);

  useEffect(() => {
    if (isSuccess && isCollection) {
      // * Redux state - collectionRestaurants[prId].data.LocationAreas
      // - set restaurant collection details location areas
      loadCollectionLocationAreas(prId);
    }
  }, [isSuccess]);

  return {
    data,
    isError,
    isLoading,
    isSuccess,
  };
};
